import { Link } from "react-router-dom";
import { ArrowUpRight, Linkedin, Mail, Bot, Mic, FlaskConical, Building2, Layers, Lightbulb } from "lucide-react";

const platformLinks = [
  { label: "Agentic Automation", href: "/solutions", icon: Bot },
  { label: "Voice AI & Sales Intelligence", href: "/solutions", icon: Mic },
  { label: "Deep Tech Research", href: "/solutions", icon: FlaskConical },
  { label: "Real Estate Technology", href: "/solutions", icon: Building2 },
  { label: "Full-Stack AI Engineering", href: "/solutions", icon: Layers },
  { label: "Strategy & Consulting", href: "/solutions", icon: Lightbulb },
];

const companyLinks = [
  { label: "About Us", href: "/about" },
  { label: "Services", href: "/services" },
  { label: "Solutions", href: "/solutions" },
  { label: "Partnerships", href: "/partnerships" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
];

export default function Footer() {
  return (
    <footer data-testid="main-footer" className="relative bg-surface-raised border-t border-white/5 text-white overflow-hidden">
      {/* Top accent gradient line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-indigo-500/40 to-transparent" />

      {/* Ambient background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[300px] bg-indigo-600/[0.04] rounded-full blur-[120px]" />
        <div className="absolute top-0 right-1/4 w-[300px] h-[200px] bg-violet-500/[0.03] rounded-full blur-[100px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-8">

        {/* Main grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-14">

          {/* Brand column */}
          <div className="md:col-span-4">
            <Link to="/" className="flex items-center gap-3 mb-5 group">
              <img
                src="/logo.webp"
                alt="brgt.ai"
                className="h-10 w-auto rounded group-hover:opacity-80 transition-opacity"
              />
              <span className="font-heading font-bold text-xl">brgt.ai</span>
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed max-w-sm mb-6">
              Our team brings deep domain expertise across pharmaceuticals, medical devices, and industrial manufacturing — translating specialized knowledge into purpose-built Agentic and Generative AI systems that deliver measurable outcomes for your business.
            </p>

            {/* Social icons */}
            <div className="flex gap-3 mb-8">
              <a
                href="https://www.linkedin.com/company/blueribbon-international"
                target="_blank"
                rel="noreferrer"
                aria-label="LinkedIn"
                className="p-2.5 rounded-full bg-white/[0.05] border border-white/10 hover:bg-indigo-600 hover:border-indigo-600 transition-all duration-200"
                data-testid="footer-linkedin"
              >
                <Linkedin size={16} />
              </a>
              <a
                href="mailto:info@brgt.ai"
                aria-label="Email us"
                className="p-2.5 rounded-full bg-white/[0.05] border border-white/10 hover:bg-indigo-600 hover:border-indigo-600 transition-all duration-200"
                data-testid="footer-email"
              >
                <Mail size={16} />
              </a>
            </div>

            {/* CTA card */}
            <div className="rounded-2xl border border-white/8 bg-white/[0.03] backdrop-blur-sm p-5">
              <p className="text-sm text-slate-300 font-medium mb-3">
                Ready to build with AI?
              </p>
              <Link
                to="/contact"
                data-testid="footer-cta"
                className="inline-flex items-center gap-2 bg-indigo-600 text-white font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-indigo-500 transition-colors active:scale-95"
              >
                Start a Conversation <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>

          {/* Platform links */}
          <div className="md:col-span-4">
            <h4 className="font-heading font-semibold text-xs uppercase tracking-widest text-indigo-400 mb-5">
              The Platform
            </h4>
            <ul className="space-y-3">
              {platformLinks.map(({ label, href, icon: Icon }) => (
                <li key={label}>
                  <Link
                    to={href}
                    className="flex items-center gap-2.5 text-slate-400 hover:text-white text-sm transition-colors group"
                  >
                    <Icon size={13} className="text-slate-500 group-hover:text-indigo-400 transition-colors flex-shrink-0" />
                    <span>{label}</span>
                    <ArrowUpRight size={11} className="opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company links */}
          <div className="md:col-span-2">
            <h4 className="font-heading font-semibold text-xs uppercase tracking-widest text-indigo-400 mb-5">
              Company
            </h4>
            <ul className="space-y-3">
              {companyLinks.map(({ label, href }) => (
                <li key={label}>
                  <Link
                    to={href}
                    className="text-slate-400 hover:text-white text-sm flex items-center gap-1 transition-colors group"
                  >
                    {label}
                    <ArrowUpRight size={11} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Tagline / mission block */}
          <div className="md:col-span-2 flex flex-col gap-4">
            <h4 className="font-heading font-semibold text-xs uppercase tracking-widest text-indigo-400">
              Our Mission
            </h4>
            <p className="text-slate-500 text-xs leading-relaxed">
              AI for every business that dares to grow.
            </p>
            <div className="mt-auto pt-4">
              <span className="inline-block text-[10px] font-mono uppercase tracking-widest text-white/15 border border-white/8 px-2.5 py-1 rounded-full">
                Built with brgt.ai
              </span>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/[0.06] pt-6 flex flex-col md:flex-row justify-between items-center gap-3">
          <p className="text-slate-500 text-xs">
            &copy; {new Date().getFullYear()} brgt.ai — BlueRibbonGlobal Tech. All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            <Link to="/about" className="text-slate-500 hover:text-slate-300 text-xs transition-colors">Privacy</Link>
            <Link to="/about" className="text-slate-500 hover:text-slate-300 text-xs transition-colors">Terms</Link>
            <span className="text-slate-600 text-xs font-mono">v2.0</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
